import { supabase } from '../lib/supabase';
import { getChatCompletion } from './aiIntegrations/chatCompletion';

// Escalation triggers - keywords that indicate need for human intervention
const ESCALATION_KEYWORDS = [
  'speak to human', 'talk to person', 'real person', 'human counselor',
  'scholarship negotiation', 'financial aid details', 'admission decision',
  'complaint', 'unhappy', 'disappointed', 'frustrated', 'angry',
  'application status', 'interview', 'final decision'
];

// Complex query indicators
const COMPLEX_INDICATORS = [
  'eligibility exception', 'special case', 'unique situation',
  'transfer credits', 'previous degree', 'work experience credit'
];

export const aiChatService = {
  /**
   * Detect if message requires human escalation
   */
  detectEscalation(message) {
    const lowerMessage = message?.toLowerCase();
    
    // Check for explicit human request
    for (const keyword of ESCALATION_KEYWORDS) {
      if (lowerMessage?.includes(keyword)) {
        return {
          shouldEscalate: true,
          reason: 'user_requested_human',
          trigger: keyword
        };
      }
    }
    
    // Check for complex queries
    for (const indicator of COMPLEX_INDICATORS) {
      if (lowerMessage?.includes(indicator)) {
        return {
          shouldEscalate: true,
          reason: 'complex_query',
          trigger: indicator
        };
      }
    }
    
    return { shouldEscalate: false };
  },

  /**
   * Build AI system prompt with institute context
   */
  async buildSystemPrompt(instituteId) {
    try {
      // Get institute profile
      const { data: institute } = await supabase
        ?.from('institute_profiles')
        ?.select('institute_name, description')
        ?.eq('user_id', instituteId)
        ?.single();
      
      // Get institute FAQs (PRIORITY KNOWLEDGE SOURCE)
      const { data: faqs } = await supabase
        ?.from('faqs')
        ?.select('question, answer, category')
        ?.eq('institute_id', instituteId)
        ?.eq('is_active', true)
        ?.order('display_order', { ascending: true })
        ?.limit(30);
      
      // Get institute courses
      const { data: courses } = await supabase
        ?.from('courses')
        ?.select('course_name, category, duration, fees, eligibility, show_fee')
        ?.eq('institute_id', instituteId)
        ?.limit(20);
      
      // Build FAQs section for prompt (PRIORITY)
      let faqsInfo = '';
      if (faqs && faqs?.length > 0) {
        const faqsByCategory = {};
        faqs?.forEach(faq => {
          if (!faqsByCategory?.[faq?.category]) {
            faqsByCategory[faq?.category] = [];
          }
          faqsByCategory?.[faq?.category]?.push(faq);
        });
        
        faqsInfo = 'Institute FAQs (Use these as your PRIMARY knowledge source):\n';
        Object.keys(faqsByCategory)?.forEach(category => {
          faqsInfo += `\n${category}:\n`;
          faqsByCategory?.[category]?.forEach(faq => {
            faqsInfo += `Q: ${faq?.question}\nA: ${faq?.answer}\n\n`;
          });
        });
      }
      
      const coursesInfo = courses?.map(c => 
        `- ${c?.course_name} (${c?.category}, ${c?.duration}, ${c?.show_fee ? `₹${c?.fees}` : 'Contact for fees'})`
      )?.join('\n') || 'No courses available';
      
      return `You are an AI assistant for ${institute?.institute_name || 'our institute'}. 

Institute Description:
${institute?.description || 'A leading educational institution'}

${faqsInfo}

Available Courses:
${coursesInfo}

Your role:
- **ALWAYS prioritize FAQ answers when available** - If a question matches an FAQ, use that exact answer
- Answer questions about courses, fees, eligibility, admission process
- Provide information about campus facilities and programs
- Be helpful, professional, and concise
- If asked about scholarships, application status, or complex eligibility cases, politely inform that a human counselor will assist
- If student seems frustrated or requests human assistance, acknowledge and inform that a counselor will take over
- Keep responses under 150 words unless detailed explanation needed
- When using FAQ information, you can say "According to our FAQs..." or reference the FAQ naturally

Important:
- **FAQs are your most reliable source** - trust them completely
- Never make up information about courses or fees not listed above
- For unlisted information, say "Let me connect you with our counselor for detailed information"
- Be warm and encouraging about the institute's programs`;
    } catch (error) {
      console.error('Error building system prompt:', error);
      return 'You are a helpful AI assistant for an educational institute. Answer questions about courses and admissions professionally.';
    }
  },

  /**
   * Get conversation history for context
   */
  async getConversationHistory(conversationId, limit = 10) {
    try {
      const { data, error } = await supabase
        ?.from('chat_messages')
        ?.select('message, sender_id, sender_type, sent_at')
        ?.eq('conversation_id', conversationId)
        ?.order('sent_at', { ascending: true })
        ?.limit(limit);
      
      if (error) throw error;
      
      // Get conversation to determine roles
      const { data: conversation } = await supabase
        ?.from('chat_conversations')
        ?.select('student_id, institute_id')
        ?.eq('id', conversationId)
        ?.single();
      
      return data?.map(msg => ({
        role: msg?.sender_type === 'ai' ? 'assistant' : 
              (msg?.sender_id === conversation?.student_id ? 'user' : 'assistant'),
        content: msg?.message
      })) || [];
    } catch (error) {
      console.error('Error getting conversation history:', error);
      return [];
    }
  },

  /**
   * Generate AI response for student message
   */
  async generateAIResponse(conversationId, studentMessage, instituteId) {
    try {
      // Check for escalation triggers
      const escalation = this.detectEscalation(studentMessage);
      
      if (escalation?.shouldEscalate) {
        // Handoff to human
        await this.handoffToHuman(conversationId, escalation?.reason);
        
        return {
          message: "I understand you need assistance from our counselor. Let me connect you with a human expert who can help you better. They'll be with you shortly!",
          shouldEscalate: true,
          escalationReason: escalation?.reason,
          senderType: 'ai'
        };
      }
      
      // Build context
      const systemPrompt = await this.buildSystemPrompt(instituteId);
      const history = await this.getConversationHistory(conversationId);
      
      // Prepare messages for AI
      const messages = [
        { role: 'system', content: systemPrompt },
        ...history,
        { role: 'user', content: studentMessage }
      ];
      
      // Get AI response
      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-5',
        messages,
        {
          max_completion_tokens: 300,
          temperature: 1
        }
      );
      
      const aiMessage = response?.choices?.[0]?.message?.content || 'I apologize, but I\'m having trouble responding right now. Let me connect you with a human counselor.';
      
      // Calculate confidence (simplified - in production, use more sophisticated methods)
      const confidence = response?.choices?.[0]?.finish_reason === 'stop' ? 0.85 : 0.60;
      
      // Save AI message to database
      const { data: savedMessage, error: saveError } = await supabase
        ?.from('chat_messages')
        ?.insert({
          conversation_id: conversationId,
          sender_id: instituteId,
          message: aiMessage,
          sender_type: 'ai',
          ai_model: 'gpt-5',
          ai_confidence: confidence,
          escalation_trigger: null,
          sent_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (saveError) throw saveError;

      // Get AI assistant name
      const { data: institute } = await supabase
        ?.from('institute_profiles')
        ?.select('ai_assistant_name')
        ?.eq('user_id', instituteId)
        ?.single();

      return {
        message: aiMessage,
        confidence,
        shouldEscalate: false,
        senderType: 'ai',
        aiName: institute?.ai_assistant_name || 'AI Assistant',
        messageId: savedMessage?.id
      };
    } catch (error) {
      console.error('Error generating AI response:', error);
      
      // Fallback to human on AI error
      await this.handoffToHuman(conversationId, 'ai_error');
      
      return {
        message: "I'm experiencing technical difficulties. Let me connect you with our counselor who can assist you directly.",
        shouldEscalate: true,
        escalationReason: 'ai_error',
        senderType: 'ai'
      };
    }
  },

  /**
   * Handoff conversation from AI to human
   */
  async handoffToHuman(conversationId, reason) {
    try {
      const { error } = await supabase?.rpc('handoff_conversation_to_human', {
        p_conversation_id: conversationId,
        p_handoff_reason: reason
      });
      
      if (error) throw error;
      
      // Get conversation details for notification
      const { data: conversation } = await supabase
        ?.from('chat_conversations')
        ?.select('institute_id, student_id')
        ?.eq('id', conversationId)
        ?.single();
      
      // Notify institute about handoff
      if (conversation) {
        const { notificationService } = await import('./notificationService');
        await notificationService?.notifyChatMessage({
          userId: conversation?.institute_id,
          conversationId,
          senderName: 'AI Assistant',
          messagePreview: 'Conversation handed off to human counselor'
        });

        // Send email notification to counsellor
        await this.sendEscalationEmail(conversationId, conversation, reason);
      }
    } catch (error) {
      console.error('Error handing off to human:', error);
      throw error;
    }
  },

  /**
   * Send email to counsellor when AI escalates a student chat
   */
  async sendEscalationEmail(conversationId, conversation, reason) {
    try {
      // Fetch institute counsellor_emails array and name
      const { data: institute } = await supabase
        ?.from('institute_profiles')
        ?.select('counsellor_emails, institute_name')
        ?.eq('user_id', conversation?.institute_id)
        ?.single();

      // Build recipient list from counsellor_emails array
      let counsellorEmails = [];
      if (institute?.counsellor_emails && institute?.counsellor_emails?.length > 0) {
        counsellorEmails = institute?.counsellor_emails?.filter(e => e && e?.trim() !== '');
      }

      // Fallback: fetch email from auth user record if no counsellor emails configured
      if (counsellorEmails?.length === 0) {
        const { data: userData } = await supabase
          ?.from('users')
          ?.select('email')
          ?.eq('id', conversation?.institute_id)
          ?.single();
        if (userData?.email) {
          counsellorEmails = [userData?.email];
        }
      }

      if (counsellorEmails?.length === 0) {
        console.warn('No counsellor emails found for institute, skipping escalation email');
        return;
      }

      // Fetch student name
      const { data: student } = await supabase
        ?.from('student_profiles')
        ?.select('name, full_name')
        ?.eq('user_id', conversation?.student_id)
        ?.single();

      const studentName = student?.name || student?.full_name || 'A student';

      // Fetch last student message as question summary
      const { data: lastMessages } = await supabase
        ?.from('chat_messages')
        ?.select('message, sender_type')
        ?.eq('conversation_id', conversationId)
        ?.eq('sender_type', 'student')
        ?.order('sent_at', { ascending: false })
        ?.limit(1);

      const lastQuestion = lastMessages?.[0]?.message || 'No message available';
      const questionSummary = lastQuestion?.length > 200
        ? lastQuestion?.substring(0, 200) + '...'
        : lastQuestion;

      // Build instant login link
      const loginLink = `https://edufaircon9190.builtwithrocket.new/institute-login`;

      const reasonLabel = reason === 'user_requested_human' ? 'Student requested a human counselor'
        : reason === 'complex_query' ? 'Complex query detected by AI'
        : reason === 'ai_error' ? 'AI encountered an error' : 'AI escalation';

      const instituteName = institute?.institute_name || 'Your Institute';

      // Compose HTML email
      const html = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #f9fafb; padding: 24px; border-radius: 8px;">
          <div style="background: #dc2626; color: white; padding: 16px 24px; border-radius: 6px 6px 0 0;">
            <h2 style="margin: 0; font-size: 20px;">⚡ Chat Escalation Alert — ${instituteName}</h2>
          </div>
          <div style="background: white; padding: 24px; border-radius: 0 0 6px 6px; border: 1px solid #e5e7eb;">
            <p style="margin: 0 0 16px; color: #374151; font-size: 15px;">A student chat has been escalated and requires your immediate attention.</p>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
              <tr>
                <td style="padding: 8px 12px; background: #f3f4f6; font-weight: 600; color: #374151; width: 40%; border-radius: 4px;">Student Name</td>
                <td style="padding: 8px 12px; color: #111827;">${studentName}</td>
              </tr>
              <tr>
                <td style="padding: 8px 12px; font-weight: 600; color: #374151;">Escalation Reason</td>
                <td style="padding: 8px 12px; color: #111827;">${reasonLabel}</td>
              </tr>
              <tr>
                <td style="padding: 8px 12px; background: #f3f4f6; font-weight: 600; color: #374151; vertical-align: top;">Last Question</td>
                <td style="padding: 8px 12px; color: #111827; font-style: italic;">"${questionSummary}"</td>
              </tr>
            </table>
            <div style="text-align: center; margin-top: 24px;">
              <a href="${loginLink}" style="background: #2563eb; color: white; padding: 12px 28px; border-radius: 6px; text-decoration: none; font-size: 15px; font-weight: 600; display: inline-block;">
                Login &amp; Respond Now →
              </a>
            </div>
            <p style="margin: 20px 0 0; color: #6b7280; font-size: 13px; text-align: center;">This is an automated alert from StudyExpo. Please respond promptly to assist the student.</p>
          </div>
        </div>
      `;

      // Call send-email edge function
      const { data: { session } } = await supabase?.auth?.getSession();
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

      // Send to all counsellor emails
      const emailPromises = counsellorEmails?.map(async (email) => {
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`
            },
            body: JSON.stringify({
              to: email,
              subject: `⚡ Chat Escalation: ${studentName} needs your attention — ${instituteName}`,
              html,
              type: 'escalation_alert'
            })
          });

          if (!response?.ok) {
            const errData = await response?.json();
            console.error(`Failed to send escalation email to ${email}:`, errData);
          } else {
            console.log(`✅ Escalation email sent to counsellor: ${email}`);
          }
        } catch (err) {
          console.error(`Error sending escalation email to ${email}:`, err);
        }
      });

      await Promise.allSettled(emailPromises);
      console.log(`✅ Escalation emails dispatched to ${counsellorEmails?.length} counsellor(s)`);
    } catch (emailError) {
      // Non-blocking: log error but don't disrupt the escalation flow
      console.error('Error sending escalation email:', emailError);
    }
  },

  /**
   * Get AI chat analytics for institute
   */
  async getInstituteAnalytics(instituteId) {
    try {
      const { data, error } = await supabase
        ?.from('institute_ai_chat_stats')
        ?.select('*')
        ?.eq('institute_id', instituteId)
        ?.single();
      
      if (error) throw error;
      
      return {
        totalConversations: data?.total_conversations || 0,
        aiResolvedCount: data?.ai_resolved_count || 0,
        humanResolvedCount: data?.human_resolved_count || 0,
        escalatedCount: data?.escalated_count || 0,
        avgAiMessages: parseFloat(data?.avg_ai_messages || 0),
        avgHumanMessages: parseFloat(data?.avg_human_messages || 0),
        avgConfidence: parseFloat(data?.avg_confidence || 0),
        aiResolutionRate: parseFloat(data?.ai_resolution_rate || 0)
      };
    } catch (error) {
      console.error('Error getting analytics:', error);
      return {
        totalConversations: 0,
        aiResolvedCount: 0,
        humanResolvedCount: 0,
        escalatedCount: 0,
        avgAiMessages: 0,
        avgHumanMessages: 0,
        avgConfidence: 0,
        aiResolutionRate: 0
      };
    }
  },

  /**
   * Get conversations by handler type for institute dashboard
   */
  async getConversationsByHandler(instituteId, handlerType = 'ai') {
    try {
      const { data, error } = await supabase
        ?.from('chat_conversations')
        ?.select(`
          *,
          student:student_id(id, name, email)
        `)
        ?.eq('institute_id', instituteId)
        ?.eq('handler_type', handlerType)
        ?.eq('status', 'active')
        ?.order('last_message_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(conv => ({
        id: conv?.id,
        studentId: conv?.student_id,
        instituteId: conv?.institute_id,
        handlerType: conv?.handler_type,
        status: conv?.status,
        lastMessageAt: conv?.last_message_at,
        aiMessageCount: conv?.ai_message_count || 0,
        humanMessageCount: conv?.human_message_count || 0,
        escalationCount: conv?.escalation_count || 0,
        handedOffAt: conv?.handed_off_at,
        handoffReason: conv?.handoff_reason,
        createdAt: conv?.created_at,
        student: conv?.student
      })) || [];
    } catch (error) {
      console.error('Error getting conversations by handler:', error);
      return [];
    }
  },

  /**
   * Manually take over AI conversation
   */
  async takeoverConversation(conversationId) {
    try {
      await this.handoffToHuman(conversationId, 'manual_takeover');
      return { success: true };
    } catch (error) {
      console.error('Error taking over conversation:', error);
      throw error;
    }
  }
};